
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import PropTypes from "prop-types";

import DataLoader from "./ui/dataLoader/DataLoader";
import MolGridHeader from "./Final-MolGrid-Header";
import MolGridDataRows from "./Final-MolGrid-data-row";
import MolPagination from "./Pagination/FinalMolPagination";
import NoRecordFound from "./ui/noRecordFound/NoRecordFound";

import "./FinalMolGrid.scss";

const FinalMolGrid = forwardRef((props, ref) => {
    // Initialize state for currentPage, pageSize, and totalPageCount
    const [sorting, setSorting] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [selectedRowIndex, setSelectedRowIndex] = useState(-1);
    const [totalPageCount, setTotalPageCount] = useState(1);
    const [pageSize, setPageSize] = useState(props.pagination?.pageSize || 10);
    const [currentPage, setCurrentPage] = useState(props.pagination?.currentPage || 1);
    const [collapsedRows, setCollapsedRows] = useState({});

    // Calculate the total page count whenever totalCount or pageSize changes
    useEffect(() => {
        if (props.pagination?.totalCount > 0) {
            calculateTotalPage(pageSize);
        }
    }, [props.pagination?.totalCount, pageSize]);

    useEffect(() => {
        // Keep only rows within the bounds of the new dataSource
        setCollapsedRows((prevState) =>
            Object.keys(prevState).reduce((acc, key) => {
                const index = parseInt(key, 10);
                if (index < props.dataSource.length) {
                    acc[key] = prevState[key];
                }
                return acc;
            }, {})
        );
    }, [props.dataSource, props.childTableDataSource]);

    // Calculate total page count and adjust currentPage if necessary
    const calculateTotalPage = (newPageSize) => {
        let newTotalPageCount = Math.ceil(
            props.pagination.totalCount / newPageSize
        );
        if (newTotalPageCount === 0) {
            newTotalPageCount = 1;
        }
        if (currentPage > newTotalPageCount) {
            setCurrentPage(newTotalPageCount);
        }
        setTotalPageCount(newTotalPageCount);
    };

    // Get the current page object
    const getCurrentPageObject = () => {
        return {
            pageNumber: currentPage,
            pageSize: pageSize,
            // Add other pagination-related properties here
        };
    };

    // Get the current page object
    const getDefaultPageObject = () => {
        setCurrentPage(1);
        return {
            pageNumber: 1,
            pageSize: pageSize,
            // Add other pagination-related properties here
        };
    };

    // get string values for sorting
    const getSortingString = (data) => {
        return data.map(item => {
            const sortingOrder = item.isAsc ? '' : ' desc';
            return `${item.fieldName}${sortingOrder}`;
        }).join(',');
    };

    // toggle row for collapsible grid
    const toggleCollapsibleRows = useCallback((rowId) => {
        setCollapsedRows((prevState) => ({
            ...prevState,
            [rowId]: !prevState[rowId],
        }));
    }, []);

    // Get the selected row
    const getSelectedRow = () => {
        return {
            row: selectedRow,
            rowIndex: selectedRowIndex
        }
    };

    // Reset the selected row
    const resetSelectedRow = () => {
        setSelectedRow({});
        setSelectedRowIndex(-1);
    }

    // Generate sorting string
    const generateSortingString = () => {
        return getSortingString(sorting);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        resetSelectedRow();
        setCurrentPage(newPage);
        if (props.onPageChange) {
            props.onPageChange({
                pageNumber: newPage,
                pageSize: pageSize,
                // Add other pagination-related properties here
            });
        }
    };

    // Update sortign on Columns
    const handleSortingUpdate = (updatedSortingData) => {
        setSorting(updatedSortingData);
        if (props.onSorting) {
            props.onSorting(getSortingString(updatedSortingData));
        }
    };

    // Handle page size change
    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        calculateTotalPage(newSize);
        setCurrentPage(1);
        if (props.onPageChange) {
            props.onPageChange({
                pageNumber: 1,
                pageSize: newSize,
                // Add other pagination-related properties here
            });
        }
    };

    const handleColumnDataChange = (fieldName, newRowData, rowIndex) => {
        props?.onColumnChange(fieldName, newRowData, rowIndex)
    }

    const handleRowEditClick = (rowData, rowIndex) => {
        props.onEditableGridEditClick?.(rowData, rowIndex);
    }

    // Handle Save Click after data Update
    const handleUpdateSaveClick = (updateData, rowIndexToUpdate) => {
        if (props?.onRowDataUpdate) {
            props?.onRowDataUpdate(updateData, rowIndexToUpdate)
        }
    }

    // handle row selection
    const handleRowSelect = (rowData, rowIndex) => {
        //    e.preventDefault(); // Prevent the default action (e.g., following a link)
        setSelectedRow(rowData);
        setSelectedRowIndex(rowIndex);
        if (props.onRowSelect) {
            props.onRowSelect(rowData, rowIndex);
        }
        // Add any additional logic here
    };

    const getGridData = () => {
        if (props?.dataSource) return props.dataSource
    }

    // Use useImperativeHandle to make the getCurrentPageObject function accessible via the ref
    useImperativeHandle(ref, () => ({
        getCurrentPageObject,
        getDefaultPageObject,
        generateSortingString,
        getSelectedRow,
        resetSelectedRow,
        getGridData,
    }));

    const renderDataRow = () => {
        if (props.isLoading) {
            return (
                <tr>
                    <td colSpan={100}>
                        <DataLoader />
                    </td>
                </tr>
            );
        }

        if (props.dataSource?.length > 0) {
            return (
                <MolGridDataRows
                    dataSource={props.dataSource}
                    childTableDataSource={props.childTableDataSource}
                    columns={props.configuration.columns}
                    onActionChange={props.onActionChange}
                    customColumnClass={props.configuration.customColumnClass}
                    customHeaderClass={props.configuration.customHeaderClass}
                    allowEditGrid={props.configuration.allowEdit}
                    editGridSettings={props.configuration.editSettings}
                    onRowDataUpdateSaving={handleUpdateSaveClick}
                    onToggleRow={toggleCollapsibleRows}
                    collapsedRows={collapsedRows}
                    onColumnDataChange={handleColumnDataChange}
                    onRowEditClick={handleRowEditClick}
                    onRowDataDelete={props.onRowDataDelete}
                    onHandleRowSelect={handleRowSelect} // Pass the handler here
                    selectedRow={selectedRow}
                    selectedRowIndex={selectedRowIndex}
                    {...props}
                />
            );
        }

        return (
            <tr>
                <td colSpan={100}>
                    <NoRecordFound />
                </td>
            </tr>
        );
    };

    return (
        <div className={`molgrid-2 ${props.configuration.hasChildGridTable ? "table-custom" : ""}`}>
            <table className={props.configuration?.gridStyle?.tableClass}>
                <thead>
                    <MolGridHeader
                        columns={props.configuration.columns}
                        onSortingUpdate={handleSortingUpdate}
                        selectedSorting={sorting}
                        hasChildGridTable={props.configuration.hasChildGridTable}
                    />
                </thead>
                <tbody>{renderDataRow()}</tbody>
            </table>
            {props.allowPagination && (
                <MolPagination
                    pageSize={pageSize}
                    currentPage={currentPage}
                    totalPages={totalPageCount}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}
        </div>
    );
});

FinalMolGrid.propTypes = {
    configuration: PropTypes.shape({
        columns: PropTypes.array.isRequired,
        allowEdit: PropTypes.bool,
        hasChildGridTable: PropTypes.bool,
        customColumnClass: PropTypes.string,
        customHeaderClass: PropTypes.string,
        editSettings: PropTypes.object,
        gridStyle: PropTypes.shape({
            tableClass: PropTypes.string,
        }),
    }).isRequired,
    dataSource: PropTypes.arrayOf(PropTypes.object),
    childTableDataSource: PropTypes.arrayOf(PropTypes.object),
    allowPagination: PropTypes.bool,
    pagination: PropTypes.shape({
        currentPage: PropTypes.number,
        totalCount: PropTypes.number,
        pageSize: PropTypes.number,
    }),
    isLoading: PropTypes.bool,
    onPageChange: PropTypes.func,
    onSorting: PropTypes.func,
    onRowDataEdit: PropTypes.func,
    onColumnChange: PropTypes.func,
    onRowDataUpdate: PropTypes.func,
    onRowDataDelete: PropTypes.func,
    onRowSelect: PropTypes.func,
    onActionChange: PropTypes.func,
    onEditableGridEditClick: PropTypes.func,

};

export default FinalMolGrid;

